import 'react-simple-phone-input/dist/style.css';

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { InputBaseProps } from '@mui/material/InputBase';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { useState } from 'react';
import {
  ControllerRenderProps,
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { PhoneInput, PhoneInputResponseType } from 'react-simple-phone-input';

import { DEFAULT_COUNTRY_CODE } from '~constants/enums';
import { useStores } from '~store';
import { matchIsValidTelOrTestTel } from '~utils/utilFunctions';

import Label from './Label';

type Props<T extends FieldValues> = UseControllerProps<T> & {
  defaultCountryCode?: CountryCode | undefined;
  inputProps?: InputBaseProps['inputProps'];
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  sx?: SxProps<Theme>;
};

const buttonStyle = (theme: Theme) => ({
  overflow: 'hidden',
  width: 'max-content',
  paddingRight: '6px', // Minimizing the width of the button
  height: '36px',
  backgroundColor: theme.semantic.surfaceInputSolid,
  color: theme.semantic.textStrong,
});

const containerStyle = (
  invalid: boolean,
  required: boolean,
  isDisabled: boolean,
  theme: Theme,
) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '2px', // 2px margin to match Mui input fields
  position: 'relative',
  boxShadow: invalid
    ? `0 0 0 2px ${theme.palette.error.main}`
    : required && !isDisabled
      ? `0 0 0 2px ${theme.palette.primary.main}`
      : `0 0 0 1px ${theme.palette.grey[500]}`, // Mid grey reads as a border against both
  // the light and the dark input fill; the semantic border tokens are tuned for
  // surfaces and drop to ~1.3:1 on white here.
  borderRadius: '4px',
  '&:hover': {
    boxShadow: invalid || required ? '' : `0 0 0 1px ${theme.semantic.textStrong}`,
  },
  '&:focus-within': {
    boxShadow: invalid ? `` : `0 0 0 2px ${theme.palette.primary.main}`,
    overflow: 'visible',
  },
  // The library hard-codes a translucent black wash that is invisible on a dark list.
  '& li:hover, & li.active': {
    backgroundColor: theme.semantic.actionHover,
  },
});

const dropdownStyle = (theme: Theme) => ({
  zIndex: 100,
  backgroundColor: theme.semantic.surfaceRaised,
  color: theme.semantic.textStrong,
  border: `1px solid ${theme.semantic.borderStrong}`,
});

const searchInputStyle = (theme: Theme) => ({
  backgroundColor: theme.semantic.surfaceInputSolid,
  color: theme.semantic.textStrong,
  border: `1px solid ${theme.semantic.borderStrong}`,
});

const inputStyle = (theme: Theme) => ({
  fontSize: '14px', // Standard font size for Mui input fields
  padding: '4px',
  // The underlying <input> defaults to a white/black browser style; drive it from
  // the theme so it matches the flag button and flips in dark mode.
  backgroundColor: theme.semantic.surfaceInputSolid,
  color: theme.semantic.textStrong,
});

export default function PhoneCodeFlagInput<T extends FieldValues>({
  control,
  defaultCountryCode,
  inputProps,
  isDisabled = false,
  isRequired,
  label,
  name,
  sx,
}: Props<T>) {
  const theme = useTheme();
  const { field, fieldState } = useController({ control, name });
  const error = fieldState.error?.message ?? fieldState.error?.root?.message;

  const { userStore } = useStores();
  const fallbackCountry =
    (userStore.userCompany?.country as CountryCode) || DEFAULT_COUNTRY_CODE;
  const effectiveDefaultCountry = defaultCountryCode || fallbackCountry;
  const emptyValue = `+${getCountryCallingCode(effectiveDefaultCountry)}`;
  const [countryCode, setCountryCode] = useState<CountryCode>(effectiveDefaultCountry);

  const handlePhoneChange = (
    e: PhoneInputResponseType,
    field: ControllerRenderProps<T, Path<T>>,
  ) => {
    if (matchIsValidTelOrTestTel(e.value)) {
      const parsedNumber = parsePhoneNumberWithError(e.value);
      if (parsedNumber?.country && parsedNumber?.country !== countryCode) {
        setCountryCode(parsedNumber.country);
      }
    }

    if (e.value === emptyValue) {
      // When the user clears the input, the value collapses to '+<dial-code>'
      // for the currently-displayed country. Treat that as empty to avoid
      // spurious validation errors.
      field.onChange(' ');
    } else {
      field.onChange(e.value);
    }
  };

  return (
    <FormControl
      error={Boolean(error)}
      sx={[
        { width: '100%', display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <Box
        sx={containerStyle(fieldState.invalid, isRequired || false, isDisabled, theme)}
      >
        <PhoneInput
          value={
            field.value && matchIsValidTelOrTestTel(field.value)
              ? (parsePhoneNumberWithError(field?.value)?.nationalNumber as string)
              : field.value
          }
          placeholder="9051231234"
          country={
            field.value &&
            matchIsValidTelOrTestTel(field.value) &&
            parsePhoneNumberWithError(field?.value)?.country
              ? (parsePhoneNumberWithError(field.value).country as string)
              : effectiveDefaultCountry
          }
          preferredCountries={['CA', 'US']}
          onChange={(e: PhoneInputResponseType) => {
            handlePhoneChange(e, field);
          }}
          inputProps={inputProps}
          inputStyle={inputStyle(theme)}
          buttonStyle={buttonStyle(theme)}
          dropdownStyle={dropdownStyle(theme)}
          searchInputStyle={searchInputStyle(theme)}
          showSearchIcon={false}
          showDropdownIcon={false}
          disableInput={isDisabled || false}
          disableDropdownOnly={isDisabled || false}
        />
        {matchIsValidTelOrTestTel((field.value as string) || '') ? (
          <CheckIcon
            style={{
              // The badge overlaps the field edge, so its fill must be opaque to mask
              // the border beneath it — the input token is transparent in light mode.
              backgroundColor: theme.semantic.surfaceRaised,
              border: `1px solid ${theme.semantic.borderStrong}`,
              borderRadius: '50%',
              color: 'green',
              height: '16px',
              position: 'absolute',
              right: '0',
              top: '0',
              transform: 'translateX(35%) translateY(-30%)',
              width: '16px',
            }}
          />
        ) : null}
      </Box>

      {fieldState.error && (
        <FormHelperText
          sx={{ mx: 0, mb: 2, mt: '2px', fontSize: '10px', fontWeight: 700 }}
        >
          {fieldState.error.message}
        </FormHelperText>
      )}
    </FormControl>
  );
}
